import React, {useMemo} from 'react';
import Translate from "../../../components/Translate";
import DropdownSelect from "../../form/DropdownSelect";
import './StatisticCard.css';
import './StatFilterCard.css';
import BotAvatar from "../../../scenes/dashboard/components/management/clients/new/overview/BotAvatar";
import Tooltip from '../Tooltip';

// searching only pays off once the list stops fitting on screen
const SEARCH_THRESHOLD = 7;

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return {text: '—', title: ''};
    if (typeof value !== 'number' || !isFinite(value)) return {text: value, title: ''};

    const full = value.toLocaleString('en-US');
    const abs = Math.abs(value);

    if (abs >= 1000000) return {text: (value / 1000000).toFixed(1) + 'M', title: full};
    if (abs >= 100000) return {text: Math.round(value / 1000) + 'K', title: full};

    return {text: full, title: ''};
};

/**
 * Stat tile whose number is scoped by a multi-select of bots.
 * `items` are {id, name}; pass `children` instead of `value` for a custom figure.
 */
function StatFilterCard({title, items = [], selectedIds = [], onToggle, onToggleAll, loading, value, children}) {

    const options = useMemo(
        () => items.map(item => ({value: item.id, label: item.name, bot: item})),
        [items]
    );

    const selectedOptions = useMemo(
        () => options.filter(o => selectedIds.includes(o.value)),
        [options, selectedIds]
    );

    const allSelected = options.length > 0 && selectedOptions.length === options.length;

    const handleSelectAll = () => {
        if (typeof onToggleAll !== 'function') return;
        onToggleAll(allSelected ? [] : options.map(o => o.value));
    };

    const renderLabel = (selected) => {
        if (!selected || selected.length === 0) return <Translate text="notSelected"/>;
        if (selected.length === options.length) return <Translate text="allBots"/>;
        if (selected.length === 1) return selected[0].label;
        return selected.length + ' / ' + options.length;
    };

    const renderValue = () => {
        if (children) return children;

        const {text, title: fullValue} = formatValue(value);
        const isFigure = typeof value === 'number' && isFinite(value);

        return (
            <Tooltip text={fullValue} needTranslate={false} disabled={!fullValue}>
                <div
                className={'stat-tile__value'
                    + (isFigure ? '' : ' stat-tile__value--text')
                    + (loading ? ' stat-tile__value--stale' : '')}
                >
                    {text}
                </div>
            </Tooltip>
        );
    };

    return (
        <div className="stat-tile stat-filter">
            <span className="stat-tile__label"><Translate text={title}/></span>

            {options.length > 0 && (
                <DropdownSelect
                    selectedValue={selectedOptions}
                    options={options}
                    onChange={(option) => onToggle(option.value)}
                    onSelectAll={typeof onToggleAll === 'function' ? handleSelectAll : null}
                    renderLabel={renderLabel}
                    searchBy={options.length > SEARCH_THRESHOLD
                        ? (option, search) => option.label.toLowerCase().includes(search.toLowerCase())
                        : null}
                    variant={'outline'}
                    styles={{width: '100%'}}
                    classes={'stat-filter__select'}
                >
                    {option => (
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0}}>
                            <BotAvatar client={option.bot} size={26} imgSize={24}/>
                            <span style={{
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis'
                            }}>{option.label}</span>
                        </div>
                    )}
                </DropdownSelect>
            )}

            {renderValue()}
        </div>
    );
}

export default StatFilterCard;



// WEBPACK FOOTER //
// ./src/components-new/general/statistics/StatFilterCard.jsx